import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import type { UseArchivePlanArgs, UseArchivePlanResult } from "./types";

export const useArchivePlan = ({
  planRecords,
  onPlanArchivedAtChanged,
}: UseArchivePlanArgs): UseArchivePlanResult => {
  const { t } = useTranslation();

  const [archivingRecordIndex, setArchivingRecordIndex] = useState<
    number | null
  >(null);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [unarchivingRecordIndex, setUnarchivingRecordIndex] = useState<
    number | null
  >(null);

  const archivingRecord =
    archivingRecordIndex !== null
      ? (planRecords[archivingRecordIndex] ?? null)
      : null;

  const isArchiveConfirmOpen = archivingRecord !== null;

  const openArchivePlan = useCallback(
    (recordIndex: number) => {
      if (!planRecords[recordIndex]) return;
      setArchivingRecordIndex(recordIndex);
    },
    [planRecords],
  );

  const closeArchivePlan = useCallback(() => {
    setArchivingRecordIndex(null);
  }, []);

  const confirmArchivePlan = useCallback(async () => {
    if (archivingRecord === null || archivingRecordIndex === null) return;
    setIsArchiveLoading(true);
    try {
      const updatedPlan = await api.archiveQuantitySurveyorFloorPlan(
        archivingRecord.id,
      );
      onPlanArchivedAtChanged?.(archivingRecordIndex, updatedPlan.archivedAt);
      setArchivingRecordIndex(null);
    } catch {
      toast.error(t("smartSurveyor.planActions.archiveFailed"));
    } finally {
      setIsArchiveLoading(false);
    }
  }, [archivingRecord, archivingRecordIndex, onPlanArchivedAtChanged, t]);

  const unarchivePlan = useCallback(
    async (recordIndex: number) => {
      const record = planRecords[recordIndex];
      if (!record || unarchivingRecordIndex !== null) return;
      setUnarchivingRecordIndex(recordIndex);
      try {
        await api.unarchiveQuantitySurveyorFloorPlan(record.id);
        onPlanArchivedAtChanged?.(recordIndex, null);
      } catch {
        toast.error(t("smartSurveyor.planActions.unarchiveFailed"));
      } finally {
        setUnarchivingRecordIndex(null);
      }
    },
    [planRecords, unarchivingRecordIndex, onPlanArchivedAtChanged, t],
  );

  return {
    isArchiveConfirmOpen,
    isArchiveLoading,
    archivingPlanLabel: archivingRecord?.name ?? "",
    unarchivingRecordIndex,
    openArchivePlan,
    closeArchivePlan,
    confirmArchivePlan,
    unarchivePlan,
  };
};
